import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import { customStyles } from "./index.styles";
import { useState } from "react";
import { colorPalette, serviceList } from "../../utils/constants";
import styled from "@emotion/styled";
import {
  Stack,
  useScrollTrigger,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useNavigation, PageId } from "../../contexts/NavigationContext";
import { useParams } from "react-router-dom";
import ServicesMenu from "./ServicesMenu";

const pages: Array<{ id: PageId; name: string }> = [
  { id: "home", name: "Home" },
  { id: "services", name: "Our Services" },
  { id: "about", name: "About us" },
  { id: "contact", name: "Contact us" },
];

interface HeaderProps {
  showOnlyHome?: boolean;
}

const Header: React.FC<HeaderProps> = ({ showOnlyHome = false }) => {
  const { activePage, navigateToPage, navigateToService } = useNavigation();
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [servicesExpanded, setServicesExpanded] = useState(false);
  const { serviceId: activeServiceId } = useParams<{ serviceId?: string }>();

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
    setServicesExpanded(false);
  };

  const handleMenuSelect = (pageId: PageId) => {
    navigateToPage(pageId);
    handleCloseNavMenu();
  };

  const handleServiceSelect = (serviceId: string) => {
    navigateToService(serviceId);
    handleCloseNavMenu();
  };

  const toggleServicesExpanded = () => {
    setServicesExpanded(!servicesExpanded);
  };

  const handleLogoClick = () => {
    navigateToPage("home");
  };

  const [headerColor, setHeaderColor] = useState("transparent");

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });

  React.useEffect(() => {
    if (trigger) {
      setHeaderColor("white");
    } else {
      setHeaderColor("transparent");
    }
  }, [trigger]);

  const StyledAppBar = styled(AppBar)({
    transition: "background-color 0.3s ease",
    backgroundColor: headerColor,
  });

  const displayPages = showOnlyHome
    ? pages.filter((page) => page.id === "home")
    : pages;

  return (
    <StyledAppBar position="fixed">
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={customStyles.toolbar}>
          <Box sx={customStyles.mainBox}>
            <Box
              sx={{ ...customStyles.innerBox, cursor: "pointer" }}
              onClick={handleLogoClick}
            >
              <Stack direction="row" alignItems="center">
                <img
                  alt="logo"
                  width={80}
                  style={customStyles.image}
                  src={`${process.env.PUBLIC_URL}/images/tabLogo.png`}
                />
                <Stack>
                  <Typography sx={customStyles.title}>Radiant</Typography>
                  <Typography sx={{ ...customStyles.title, mt: -1 }}>
                    Property Management
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            <Box sx={customStyles.pageButtonsBox}>
              {displayPages.map((page, index) => {
                if (page.id === "services") {
                  return (
                    <ServicesMenu
                      key={index}
                      activePage={activePage}
                      pageButtonStyle={customStyles.pageButton}
                      serviceMenuItemStyle={customStyles.serviceMenuItem}
                      onServiceSelect={handleServiceSelect}
                    />
                  );
                }
                return (
                  <Button
                    key={index}
                    onClick={() => handleMenuSelect(page.id)}
                    sx={[
                      customStyles.pageButton,
                      {
                        color:
                          activePage === page.id
                            ? colorPalette.headerBackground
                            : "black",
                      },
                    ]}
                  >
                    {page.name}
                  </Button>
                );
              })}
            </Box>

            <Box sx={customStyles.menuButton}>
              <IconButton
                size="large"
                onClick={handleOpenNavMenu}
                color="inherit"
                aria-label="menu"
              >
                <MenuIcon sx={customStyles.icon} />
              </IconButton>

              <Menu
                anchorEl={anchorElNav}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                keepMounted
                disableAutoFocusItem
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                sx={{
                  display: { xs: "block", sm: "none" },
                  "& .MuiBackdrop-root": {
                    backgroundColor: "rgba(0, 0, 0, 0.3)",
                  },
                }}
                slotProps={{
                  paper: {
                    elevation: 8,
                    sx: {
                      width: "100vw",
                      maxWidth: "100vw",
                      left: "0 !important",
                      right: "0 !important",
                      top: "64px !important",
                      maxHeight: "calc(100vh - 64px)",
                      overflowY: "auto",
                      backgroundColor: "white",
                      borderRadius: 0,
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    },
                  },
                }}
                TransitionProps={{
                  timeout: 300,
                }}
                MenuListProps={{
                  onClick: (e) => {
                    // Only close menu if clicking on a non-service menu item
                    const target = e.target as HTMLElement;
                    const isServiceMenuItem = target.closest(
                      "[data-service-item]"
                    );
                    const isServicesToggle = target.closest(
                      "[data-services-toggle]"
                    );

                    if (!isServiceMenuItem && !isServicesToggle) {
                      handleCloseNavMenu();
                    }
                  },
                }}
              >
                {displayPages.map((page, index) => {
                  if (page.id === "services") {
                    return (
                      <Box key={index}>
                        <MenuItem
                          sx={customStyles.menuItem}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleServicesExpanded();
                          }}
                          data-services-toggle="true"
                        >
                          <Typography
                            sx={{
                              ...customStyles.menuText,
                              color:
                                activePage === page.id
                                  ? colorPalette.headerBackground
                                  : "inherit",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              width: "100%",
                            }}
                          >
                            {page.name}
                            {servicesExpanded ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </Typography>
                        </MenuItem>
                        <Collapse in={servicesExpanded} timeout={300}>
                          <List
                            component="div"
                            disablePadding
                            sx={{ backgroundColor: "#f9f9f9" }}
                          >
                            {serviceList.map((service) => (
                              <ListItemButton
                                key={service.id}
                                data-service-item="true"
                                sx={{
                                  pl: 6,
                                  py: 1.5,
                                  backgroundColor:
                                    activeServiceId === service.id
                                      ? `${colorPalette.headerBackground}10`
                                      : "transparent",
                                  borderLeft:
                                    activeServiceId === service.id
                                      ? `3px solid ${colorPalette.headerBackground}`
                                      : "3px solid transparent",
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    backgroundColor: `${colorPalette.headerBackground}08`,
                                  },
                                }}
                                onClick={() => handleServiceSelect(service.id)}
                              >
                                <ListItemText
                                  primary={service.name}
                                  primaryTypographyProps={{
                                    sx: {
                                      fontFamily: "'Barlow', sans-serif",
                                      fontWeight:
                                        activeServiceId === service.id
                                          ? 500
                                          : 300,
                                      fontSize: 16,
                                      color:
                                        activeServiceId === service.id
                                          ? colorPalette.headerBackground
                                          : "inherit",
                                    },
                                  }}
                                />
                              </ListItemButton>
                            ))}
                          </List>
                        </Collapse>
                      </Box>
                    );
                  }

                  return (
                    <MenuItem
                      sx={customStyles.menuItem}
                      key={index}
                      onClick={() => handleMenuSelect(page.id)}
                    >
                      <Typography
                        sx={{
                          ...customStyles.menuText,
                          color:
                            activePage === page.id
                              ? colorPalette.headerBackground
                              : "inherit",
                        }}
                      >
                        {page.name}
                      </Typography>
                    </MenuItem>
                  );
                })}
              </Menu>
            </Box>
          </Box>
        </Toolbar>
      </Container>
    </StyledAppBar>
  );
};

export default Header;
